import React, { useEffect, useState } from "react";
import { Card, CardBody, CardTitle, Row, Col } from "reactstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const MarketplaceMetrics = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

  const fetchMarketplaceMetrics = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("Access token not found. Please log in as admin.");
      }

      const response = await fetch(`${API_BASE_URL}/admin/dashboard/marketplace-metrics`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Unauthorized: Please log in as admin.");
        } else {
          throw new Error(`Error fetching marketplace metrics: ${response.statusText}`);
        }
      }

      const data = await response.json();
      setMetrics(data);
    } catch (err) {
      console.error("Marketplace metrics fetch error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarketplaceMetrics();
  }, []);

  if (loading) return <div className="p-4"><p>Loading Marketplace Metrics...</p></div>;
  if (error) return <div className="p-4"><p style={{ color: "red" }}>{error}</p></div>;
  if (!metrics) return <div className="p-4"><p style={{ color: "red" }}>No data available.</p></div>;

  return (
    <div className="p-4">
      <h2 className="mb-4">Marketplace Metrics</h2>

      {/* Main Metrics Cards */}
      <Row className="mb-4">
        <Col md="3" sm="6" className="mb-3">
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h6" className="text-muted">
                Active Listings
              </CardTitle>
              <h4 className="fw-bold text-success">{metrics.activeListings?.toLocaleString() || 0}</h4>
            </CardBody>
          </Card>
        </Col>
        <Col md="3" sm="6" className="mb-3">
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h6" className="text-muted">
                Sold Listings
              </CardTitle>
              <h4 className="fw-bold text-primary">{metrics.soldListings?.toLocaleString() || 0}</h4>
            </CardBody>
          </Card>
        </Col>
        <Col md="3" sm="6" className="mb-3">
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h6" className="text-muted">
                Sell-Through Rate
              </CardTitle>
              <h4 className="fw-bold">{metrics.sellThroughRate?.toFixed(2) || 0}%</h4>
            </CardBody>
          </Card>
        </Col>
        <Col md="3" sm="6" className="mb-3">
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h6" className="text-muted">
                Pending Verifications
              </CardTitle>
              <h4 className="fw-bold text-warning">{metrics.pendingListingVerifications?.toLocaleString() || 0}</h4>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* New Listings Cards */}
      <Row className="mb-4">
        <Col md="4" sm="6" className="mb-3">
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h6" className="text-muted">
                New Listings (24 Hours)
              </CardTitle>
              <h4 className="fw-bold">{metrics.newListings?.oneDay?.toLocaleString() || 0}</h4>
            </CardBody>
          </Card>
        </Col>
        <Col md="4" sm="6" className="mb-3">
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h6" className="text-muted">
                New Listings (Past Week)
              </CardTitle>
              <h4 className="fw-bold">{metrics.newListings?.oneWeek?.toLocaleString() || 0}</h4>
            </CardBody>
          </Card>
        </Col>
        <Col md="4" sm="6" className="mb-3">
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h6" className="text-muted">
                New Listings (Past Month)
              </CardTitle>
              <h4 className="fw-bold">{metrics.newListings?.oneMonth?.toLocaleString() || 0}</h4>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Average Time Metrics */}
      <Row className="mb-4">
        <Col md="6" className="mb-3">
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h6" className="text-muted">
                Average Time to First Message
              </CardTitle>
              {metrics.averageTimeToFirstMessage ? (
                <div>
                  <h4 className="fw-bold">{metrics.averageTimeToFirstMessage.formatted}</h4>
                  <small className="text-muted">({metrics.averageTimeToFirstMessage.hours.toFixed(2)} hours)</small>
                </div>
              ) : (
                <h4 className="fw-bold text-muted">N/A</h4>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col md="6" className="mb-3">
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h6" className="text-muted">
                Average Time to First Sale
              </CardTitle>
              {metrics.averageTimeToFirstSale ? (
                <div>
                  <h4 className="fw-bold">{metrics.averageTimeToFirstSale.formatted}</h4>
                  <small className="text-muted">({metrics.averageTimeToFirstSale.hours.toFixed(2)} hours)</small>
                </div>
              ) : (
                <h4 className="fw-bold text-muted">N/A</h4>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Chart */}
      {metrics.chart?.listingsOverTime && metrics.chart.listingsOverTime.length > 0 && (
        <Row className="mt-4">
          <Col md="12" className="mb-4">
            <Card className="shadow-sm border-0">
              <CardBody>
                <CardTitle tag="h5" className="mb-3">
                  Listings Over Time (Active vs Sold)
                </CardTitle>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={metrics.chart.listingsOverTime}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="activeCount" fill="#28a745" name="Active" />
                    <Bar dataKey="soldCount" fill="#dc3545" name="Sold" />
                  </BarChart>
                </ResponsiveContainer>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default MarketplaceMetrics;
